import { useState } from "react";
import {
  useListCustomers,
  useCreateCustomer,
  useGetCustomer,
  useDeleteCustomer,
  getListCustomersQueryKey,
  customFetch,
} from "@workspace/api-client-react";
import { aniversarioParaMostrar, formatCurrency, formatWhatsApp, waPhone, orderDisplayCode } from "@/lib/utils";
import { FalhaAoCarregar } from "@/components/admin/FalhaAoCarregar";
import { cliqueNoLinkDoWhatsApp } from "@/lib/abreWhatsApp";
import { titleCasePtBR } from "@/lib/textNormalize";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { extractApiErrorMessage } from "@/lib/api-error";
import { WhatsAppInput } from "@/components/ui/whatsapp-input";
import { Card } from "@/components/ui/card";
import { BirthdaySection } from "@/components/admin/BirthdaySection";
import { EditCustomerModal } from "@/components/admin/EditCustomerModal";
import { PontosDaLoja, PontosDoCliente } from "@/components/admin/LoyaltyPanel";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Users,
  Search,
  Plus,
  X,
  MessageCircle,
  ShoppingBag,
  ArrowLeft,
  Loader2,
  UserCircle2,
  Trash2,
  Pencil,
  Download,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

export default function Customers() {
  const [search, setSearch] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const lojaStore = user?.store as { birthdayReminders?: boolean | null; birthdayAdvanceDays?: number | null; birthdayPastDays?: number | null } | undefined;

  const { data: customers, isLoading, error: erroClientes, refetch: recarregarClientes, isFetching: buscandoClientes } = useListCustomers(
    search ? { search } : undefined
  );

  // Exporta respeitando a busca da tela (mesmo padrão do Financeiro e Pedidos).
  const handleExport = async () => {
    setExporting(true);
    try {
      const qs = search ? `?search=${encodeURIComponent(search)}` : "";
      const blob = await customFetch<Blob>(`/api/customers/export${qs}`, {
        responseType: "blob",
      } as any);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "clientes.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast({
        title: "Não foi possível exportar os clientes",
        description: "Tente novamente em instantes.",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  if (selectedId) {
    return (
      <CustomerDetail
        id={selectedId}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Clientes</h1>
          <p className="text-muted-foreground mt-1">
            Gerencie os clientes da sua loja.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={exporting}
            data-testid="customers-export"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Exportar Excel
          </Button>
          <Button
            onClick={() => setShowAddForm(true)}
            className="shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30 transition-all hover:-translate-y-0.5"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Cliente
          </Button>
        </div>
      </div>

      {!!lojaStore?.birthdayReminders && (
        <BirthdaySection next={lojaStore?.birthdayAdvanceDays ?? 3} past={lojaStore?.birthdayPastDays ?? 2} message={(lojaStore as any)?.birthdayWhatsappMessage} />
      )}

      {(lojaStore as any)?.loyaltyEnabled === true && (
        <PontosDaLoja storeName={(lojaStore as any)?.name ?? "loja"} storeSlug={(lojaStore as any)?.slug ?? ""} />
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nome..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {showAddForm && (
        <AddCustomerForm onClose={() => setShowAddForm(false)} />
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-36 rounded-xl" />
          ))}
        </div>
      ) : erroClientes && !customers ? (
        // Falhou sem nada guardado: aviso de conexão, não «Nenhum cliente encontrado» (ver FalhaAoCarregar).
        <FalhaAoCarregar tela="Clientes" erro={erroClientes} onTentar={() => recarregarClientes()} tentando={buscandoClientes} />
      ) : !customers || customers.length === 0 ? (
        <Card className="p-12 flex flex-col items-center justify-center text-center">
          <Users className="w-16 h-16 text-muted-foreground/30 mb-4" />
          <h3 className="text-lg font-semibold">Nenhum cliente encontrado</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {search
              ? "Tente outra busca."
              : "Adicione seu primeiro cliente ou aguarde pedidos pela loja."}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {customers.map((customer) => (
            <Card
              key={customer.id}
              className="p-5 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedId(customer.id)}
            >
              {/* <md: a coluna de botões ao lado esmagava nome/telefone/data numa
            palavra por linha — os botões descem pra uma linha própria. */}
        <div className="flex flex-wrap items-start gap-4">
                {customer.avatarBase64 ? (
                  <img
                    src={customer.avatarBase64}
                    alt={customer.firstName}
                    className="w-12 h-12 rounded-full object-cover shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <UserCircle2 className="w-6 h-6 text-primary" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">
                    {customer.firstName}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {String(customer.whatsappNumber || "").startsWith("__avulso__") ? "Sem WhatsApp" : formatWhatsApp(customer.whatsappNumber)}
                  </p>
                  {(customer as any).birthDate && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      🎂 {aniversarioParaMostrar(String((customer as any).birthDate))}
                    </p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <ShoppingBag className="w-3 h-3" />
                      {customer.orderCount} pedidos
                    </span>
                    <span className="font-medium text-foreground">
                      {formatCurrency(Number(customer.totalSpent))}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function AddCustomerForm({ onClose }: { onClose: () => void }) {
  const [firstName, setFirstName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [ddi, setDdi] = useState("55");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const createMutation = useCreateCustomer({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getListCustomersQueryKey(),
        });
        toast({
          title: "Cliente adicionado",
          description: "O cliente foi cadastrado com sucesso.",
        });
        onClose();
      },
      onError: (err: any) => {
        toast({ title: "Erro", description: extractApiErrorMessage(err, "Erro ao cadastrar cliente"), variant: "destructive" });
      },
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const digits = whatsapp.replace(/\D/g, "");
    if (!firstName.trim() || !digits) return;
    createMutation.mutate({
      data: { firstName: firstName.trim(), whatsappNumber: digits, ddi },
    });
  };

  return (
    <Card className="p-6 border-accent/20 bg-accent/5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-lg">Novo Cliente</h3>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="customerName">Nome</Label>
          <Input
            id="customerName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onBlur={(e) => setFirstName(titleCasePtBR(e.target.value))}
            autoCapitalize="words"
            placeholder="Nome do cliente"
            required
            autoFocus
          />
        </div>
        <div>
          <Label htmlFor="customerWhatsapp">WhatsApp</Label>
          <WhatsAppInput
            id="customerWhatsapp"
            value={whatsapp}
            onChange={setWhatsapp}
            ddi={ddi}
            onDdiChange={setDdi}
            required
          />
          <p className="text-xs text-muted-foreground mt-1">
            O número será verificado no WhatsApp antes de cadastrar.
          </p>
        </div>
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Verificando...
              </>
            ) : (
              "Cadastrar"
            )}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function CustomerDetail({
  id,
  onBack,
}: {
  id: string;
  onBack: () => void;
}) {
  const { data: customer, isLoading } = useGetCustomer(id);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOrders, setDeleteOrders] = useState(true);
  const [editing, setEditing] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: storeSettings } = useQuery<any>({
    queryKey: ["settings"],
    queryFn: () => customFetch("/api/settings"),
    staleTime: 60_000,
  });
  const orderCodeStyle = (storeSettings as any)?.orderCodeStyle as string | undefined;

  const deleteMutation = useDeleteCustomer({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListCustomersQueryKey() });
        toast({
          title: "Cliente excluído",
          description: "O cliente foi removido com sucesso.",
        });
        setConfirmOpen(false);
        onBack();
      },
      onError: (err: any) => {
        toast({ title: "Erro", description: extractApiErrorMessage(err, "Erro ao excluir cliente"), variant: "destructive" });
      },
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-48 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
        </Button>
        <p className="text-muted-foreground">Cliente não encontrado.</p>
      </div>
    );
  }

  // Venda de balcão grava o marcador __avulso__<storeId> no campo de número —
  // exibir isso formatado viraria um "telefone" de 26 dígitos.
  const semWhatsApp = String(customer.whatsappNumber || "").startsWith("__avulso__");
  const whatsappLink = `https://api.whatsapp.com/send?phone=${waPhone(customer.whatsappNumber)}`;

  const orderCount = customer.orderCount ?? 0;

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="-ml-2">
          <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setDeleteOrders(true);
            setConfirmOpen(true);
          }}
          className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Excluir cliente
        </Button>
      </div>

      <Card className="p-6">
        {/* <md: a coluna de botões ao lado esmagava nome/telefone/data numa
            palavra por linha — os botões descem pra uma linha própria. */}
        <div className="flex flex-wrap items-start gap-4">
          {customer.avatarBase64 ? (
            <img
              src={customer.avatarBase64}
              alt={customer.firstName}
              className="w-16 h-16 rounded-full object-cover"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <UserCircle2 className="w-8 h-8 text-primary" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold">{customer.firstName}</h2>
            <p className="text-muted-foreground">
              {semWhatsApp ? "Sem WhatsApp" : formatWhatsApp(customer.whatsappNumber)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Cliente desde{" "}
              {format(parseISO(customer.createdAt as string), "dd 'de' MMMM 'de' yyyy", {
                locale: ptBR,
              })}
            </p>
          </div>
          {/* grid metade a metade: flex-1 distribuía sobra, não igualava. */}
          <div className="grid grid-cols-2 gap-2 basis-full sm:flex sm:basis-auto">
            <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => setEditing(true)} data-testid="customer-edit-button">
              <Pencil className="w-4 h-4 mr-2" />
              Editar
            </Button>
            {!semWhatsApp && (
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" onClick={cliqueNoLinkDoWhatsApp}>
                <Button variant="outline" size="sm" className="w-full">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </a>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="bg-muted/50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold">{customer.orderCount}</p>
            <p className="text-xs text-muted-foreground">Pedidos</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-primary">
              {formatCurrency(Number(customer.totalSpent))}
            </p>
            <p className="text-xs text-muted-foreground">Total gasto</p>
          </div>
        </div>
      </Card>

      <PontosDoCliente customerId={customer.id} avulso={semWhatsApp} />

      <div>
        <h3 className="font-bold text-lg mb-4">Histórico de Pedidos</h3>
        {!customer.orders || customer.orders.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">
            <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p>Nenhum pedido registrado.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {customer.orders.map((order: any) => (
              <Card key={order.id} className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-semibold text-sm">
                      Pedido #{orderDisplayCode(order, orderCodeStyle)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {format(
                        parseISO(order.createdAt),
                        "dd/MM/yyyy 'às' HH:mm",
                        { locale: ptBR }
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary">
                      {formatCurrency(parseFloat(order.total))}
                    </p>
                    <span className="inline-block px-2 py-0.5 mt-1 rounded text-[10px] font-medium bg-muted border capitalize">
                      {order.status}
                    </span>
                  </div>
                </div>
                {order.items && order.items.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
                    {order.items.map((item: any) => (
                      <div
                        key={item.id}
                        className="flex justify-between text-sm text-muted-foreground"
                      >
                        <span>
                          {item.quantity}x {item.productName || "Produto"}
                        </span>
                        <span>{formatCurrency((parseFloat(item.price || "0") + parseFloat(item.optionsTotal || "0")) * parseInt(item.quantity || "1", 10))}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {editing && <EditCustomerModal customer={customer} onClose={() => setEditing(false)} />}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir {customer.firstName}?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O cadastro do cliente será removido permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {orderCount > 0 && (
            <div className="flex items-start justify-between gap-4 rounded-lg border bg-muted/40 p-4">
              <div className="flex-1">
                <p className="font-medium text-sm">
                  Excluir também os {orderCount} pedido{orderCount === 1 ? "" : "s"}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Os pedidos deste cliente serão removidos junto. Se desativar, o cliente não pode ser excluído enquanto tiver pedidos.
                </p>
              </div>
              <Switch
                checked={deleteOrders}
                onCheckedChange={setDeleteOrders}
                aria-label="Excluir pedidos junto"
              />
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending || (orderCount > 0 && !deleteOrders)}
              onClick={(e) => {
                e.preventDefault();
                deleteMutation.mutate({
                  id,
                  params: { deleteOrders: orderCount > 0 ? deleteOrders : undefined },
                });
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                "Excluir"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
